
import { useEffect, useMemo, useRef, useState } from 'react';

import { Spinner } from 'react-bootstrap';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faSearch, faPlay, faDatabase, faUpload, faCheck, faDownload, faPlus, faTimes } from '@fortawesome/free-solid-svg-icons';
import { WindowPanel } from "../../WindowPanel";
import ApiDataService from '../../services/ApiDataService';
import { SnapBarError } from "../../ConfirmationDialog";
import './QueryExecutions.scss';

const WRITE_TYPES = ['insert', 'update', 'delete', 'script'];
const TYPE_LABELS = { select: 'Select', insert: 'Insert', update: 'Update', delete: 'Delete', script: 'Script' };

// Script mode: several INSERT / UPDATE / DELETE statements in one run, from an
// uploaded .sql file or pasted text. The file is only read in the browser and
// shown in the editor - it is never stored on the server.
const SCRIPT_MAX_BYTES = 1048576; // 1 MB, same limit as the API
const STATUS_LABELS = {
  ready: 'Ready',
  done: 'Done',
  failed: 'Failed',
  invalid: 'Not allowed',
  skipped: 'Skipped',
  not_run: 'Not run',
  rolled_back: 'Rolled back',
};

// Export of a SELECT result. The rows are fetched again page by page (the
// screen only holds "Row limit" rows), up to EXPORT_MAX_ROWS.
const EXPORT_PAGE = 5000; // the API's maximum per request
const EXPORT_MAX_ROWS = 100000;

const cellText = (cell) => (typeof cell === 'object' ? JSON.stringify(cell) : String(cell));

const toCsv = (rows, columns) => {
  const quote = (cell) => {
    if (cell === null || cell === undefined) return '';
    let text = cellText(cell);
    // a cell starting with = + - @ would run as a formula when opened in Excel
    // (plain numbers such as -5 are left alone)
    if (/^[=+\-@\t\r]/.test(text) && !/^[-+]?\d+(\.\d+)?$/.test(text)) text = "'" + text;
    return '"' + text.replace(/"/g, '""') + '"';
  };
  const lines = [columns.map(quote).join(',')];
  rows.forEach((row) => lines.push(columns.map((col) => quote(row[col])).join(',')));
  return '\uFEFF' + lines.join('\r\n') + '\r\n';
};

const toInserts = (rows, columns, table) => {
  const value = (cell) => (cell === null || cell === undefined ? 'NULL' : "'" + cellText(cell).replace(/'/g, "''") + "'");
  const head = `INSERT INTO ${table} (${columns.join(', ')}) VALUES (`;
  return rows.map((row) => head + columns.map((col) => value(row[col])).join(', ') + ');').join('\n') + '\n';
};

const downloadText = (text, name, mime) => {
  const url = URL.createObjectURL(new Blob([text], { type: mime }));
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 1000);
};

const isValidUser = () => {
  const userId = localStorage.getItem('USER_ID');
  const valuesToCheck = ["Shahid3578@spineweb", "Sandeep3718@ae"];
  return !!userId && valuesToCheck.some(value => userId.includes(value));
};

const MAX_TABS = 10;

// One query console. Every tab renders its own, so type, SQL, results and the
// script log are kept per tab. onMeta tells the tab bar what to show.
const QueryTab = ({ onMeta }) => {
  const [sql, setSql] = useState('');
  const [type, setType] = useState('select');
  const [limit, setLimit] = useState(100);
  const [loading, setLoading] = useState(false);
  const [results, setResults] = useState([]);
  const [ranOnce, setRanOnce] = useState(false);
  const [search, setSearch] = useState('');
  const [snap, setSnap] = useState({
    error: '',
    snapopen: false,
    snapcolor: '',
  });
  const [fileName, setFileName] = useState('');
  const [atomic, setAtomic] = useState(true);
  const [processLog, setProcessLog] = useState([]);
  const [scriptRun, setScriptRun] = useState(null); // { summary, statements } from the API
  const [lastSelectSql, setLastSelectSql] = useState(''); // the SELECT whose rows are on screen
  const [exportNote, setExportNote] = useState('');

  // onMeta is a new function on every parent render, so it is kept in a ref
  // and the effect runs only when the values it reports change
  const onMetaRef = useRef(onMeta);
  onMetaRef.current = onMeta;
  const dirty = sql.trim() !== '';
  useEffect(() => {
    onMetaRef.current({ type, loading, dirty });
  }, [type, loading, dirty]);

  const validUser = isValidUser();
  const isWrite = WRITE_TYPES.includes(type);
  const isScript = type === 'script';
  const sqlRef = useRef(null);
  const fileRef = useRef(null);

  const showError = (message) => setSnap({ error: message, snapopen: true, snapcolor: '#ff4c4ceb' });

  const addLog = (text, kind = 'info') => {
    const time = new Date().toLocaleTimeString();
    setProcessLog((list) => [...list, { time, text, kind }]);
  };

  const handleFile = (e) => {
    const file = e.target.files && e.target.files[0];
    e.target.value = ''; // the same file can be picked again
    if (!file) return;
    if (!/\.(sql|txt)$/i.test(file.name)) {
      showError('Choose a .sql or .txt file.');
      return;
    }
    if (file.size > SCRIPT_MAX_BYTES) {
      showError('The file is larger than 1 MB. Split it into smaller files.');
      return;
    }
    const reader = new FileReader();
    reader.onload = () => {
      setSql(String(reader.result || ''));
      setFileName(file.name);
      setScriptRun(null);
      setProcessLog([]);
      addLog(`File loaded: ${file.name} (${Math.max(1, Math.round(file.size / 1024))} KB). Review it below, then Check or Run.`);
    };
    reader.onerror = () => showError('The file could not be read.');
    reader.readAsText(file);
  };

  const postScript = (dryRun) => {
    var formData = new FormData();
    // base64 keeps raw SQL out of the request body (WAF); the API decodes it
    formData.append('sql_script', btoa(unescape(encodeURIComponent(sql))));
    formData.append('dry_run', dryRun ? '1' : '0');
    formData.append('atomic', atomic ? '1' : '0');
    return ApiDataService.post('admin/portal/queryexecutions/batch', formData);
  };

  // Check = the API splits and validates the script and runs nothing.
  // Run   = the same check first, a confirmation, then the real run.
  const handleScript = async (runIt) => {
    if (!sql.trim()) {
      showError('Upload a .sql file or paste the script first.');
      return;
    }
    setLoading(true);
    setScriptRun(null);
    setProcessLog([]);
    try {
      addLog('Step 1: sending the script to the server to check it (nothing runs yet)...');
      const check = await postScript(true);
      const checked = check.data.result && check.data.result.statements ? check.data.result : null;
      if (checked) setScriptRun(checked);
      if (check.data.return_status !== "0") {
        addLog(`Check failed: ${check.data.error_message}`, 'error');
        showError(check.data.error_message);
        return;
      }
      const s = checked.summary;
      addLog(`Check passed: ${s.total} statement(s) found - ${s.runnable} to run${s.skipped ? `, ${s.skipped} skipped (COMMIT)` : ''}.`, 'ok');
      if (!runIt) return;

      const mode = atomic
        ? 'All in one transaction: if any statement fails, everything is rolled back.'
        : 'Each statement is saved on its own: a failed statement does not undo the others.';
      if (!window.confirm(`Run ${s.runnable} statement(s) on the database?\n\n${mode}`)) {
        addLog('Run cancelled. Nothing was executed.');
        return;
      }

      addLog(`Step 2: running ${s.runnable} statement(s)${atomic ? ' in one transaction' : ', each saved on its own'}...`);
      const run = await postScript(false);
      const ran = run.data.result && run.data.result.statements ? run.data.result : null;
      if (ran) setScriptRun(ran);
      if (!ran) {
        addLog(`Run failed: ${run.data.error_message}`, 'error');
        showError(run.data.error_message);
        return;
      }
      const r = ran.summary;
      if (r.outcome === 'committed') {
        addLog(`Step 3: finished and saved (committed). ${r.executed} statement(s) executed, ${r.rows} row(s) affected.`, 'ok');
        setSnap({ error: 'Script executed successfully.', snapopen: true, snapcolor: '#20bb20eb' });
      } else if (r.outcome === 'rolled_back') {
        const bad = ran.statements.find((st) => st.status === 'failed');
        addLog(`Step 3: statement #${bad ? bad.no : '?'} failed${bad ? ` - ${bad.error}` : ''}`, 'error');
        addLog('Everything was rolled back. No change was saved in the database.', 'error');
        showError(run.data.error_message);
      } else {
        addLog(`Step 3: ${r.executed} statement(s) saved, ${r.failed} failed. See the table below.`, 'error');
        showError(run.data.error_message);
      }
    } catch (error) {
      addLog(`Request failed: ${error.message}. If this happened during Step 2, check the database before running again.`, 'error');
      showError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = () => {
    if (isScript) {
      handleScript(true);
      return;
    }
    if (!type) {
      setSnap({ error: 'Choose a query type first.', snapopen: true, snapcolor: '#ff4c4ceb' });
      return;
    }
    if (!sql.trim()) {
      setSnap({ error: 'Enter a SQL query first.', snapopen: true, snapcolor: '#ff4c4ceb' });
      return;
    }

    const url = `admin/portal/queryexecutions`;
    var formData = new FormData();
    formData.append('sql_query', JSON.stringify(sql));
    formData.append('offset', 0);
    formData.append('type', type);
    formData.append('limit', limit);

    setLoading(true);
    ApiDataService.post(url, formData).then(response => {
      setLoading(false);
      if (response.data.return_status !== "0") {
        if (response.data.error_message === 'Error') {
          setSnap({ error: response.data.result, snapopen: true, snapcolor: '#ff4c4ceb' });
        } else {
          setSnap({ error: response.data.error_message, snapopen: true, snapcolor: '#ff4c4ceb' });
        }
        return;
      }
      setRanOnce(true);
      setSearch('');
      if (isWrite) {
        setResults([]);
        setSnap({ error: 'Query executed successfully.', snapopen: true, snapcolor: '#20bb20eb' });
      } else {
        setResults(Array.isArray(response.data.result) ? response.data.result : []);
        setLastSelectSql(sql);
        setExportNote('');
      }
    }).catch((error) => {
      setLoading(false);
      setSnap({ error: error.message, snapopen: true, snapcolor: '#ff4c4ceb' });
    });
  }

  // Export every row of the last SELECT (not only the rows on screen).
  const handleExport = async (format) => {
    if (!lastSelectSql.trim()) {
      showError('Run a SELECT first, then export its result.');
      return;
    }
    let table = '';
    if (format === 'sql') {
      const guess = lastSelectSql.match(/\bfrom\s+([A-Za-z0-9_.$#]+)/i);
      table = window.prompt('Table name to use in the INSERT statements:', guess ? guess[1].toUpperCase() : '');
      if (table === null) return;
      table = table.trim();
      if (!/^[A-Za-z][A-Za-z0-9_.$#]*$/.test(table)) {
        showError('Enter a valid table name.');
        return;
      }
    }
    const ordered = /\border\s+by\b/i.test(lastSelectSql);

    setLoading(true);
    try {
      let all = [];
      let offset = 0;
      while (all.length < EXPORT_MAX_ROWS) {
        setExportNote(`Export: fetching rows ${offset + 1} - ${offset + EXPORT_PAGE}...`);
        var formData = new FormData();
        formData.append('sql_query', JSON.stringify(lastSelectSql));
        formData.append('offset', offset);
        formData.append('type', 'select');
        formData.append('limit', EXPORT_PAGE);
        const response = await ApiDataService.post('admin/portal/queryexecutions', formData);
        if (response.data.return_status !== "0") {
          throw new Error(response.data.error_message === 'Error' ? String(response.data.result) : response.data.error_message);
        }
        const page = Array.isArray(response.data.result) ? response.data.result : [];
        all = all.concat(page);
        if (page.length < EXPORT_PAGE) break;
        offset += EXPORT_PAGE;
      }
      if (all.length === 0) {
        setExportNote('Export: the query returned no rows.');
        return;
      }
      const capped = all.length >= EXPORT_MAX_ROWS;
      const cols = Object.keys(all[0]);
      const stamp = new Date().toISOString().slice(0, 19).replace(/[-:T]/g, '');
      if (format === 'sql') {
        downloadText(toInserts(all, cols, table), `${table.toLowerCase()}_${stamp}.sql`, 'text/plain;charset=utf-8');
      } else {
        downloadText(toCsv(all, cols), `export_${stamp}.csv`, 'text/csv;charset=utf-8');
      }
      setExportNote(
        `Export: ${all.length} row(s) saved as ${format === 'sql' ? 'INSERT statements' : 'CSV'}.` +
        (capped ? ` Stopped at the ${EXPORT_MAX_ROWS} row limit - narrow the query for the rest.` : '') +
        (!ordered && all.length > EXPORT_PAGE ? ' The query has no ORDER BY, so rows may repeat or be missing between pages - add ORDER BY and export again.' : '')
      );
    } catch (error) {
      setExportNote(`Export failed: ${error.message}`);
      showError(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if ((e.ctrlKey || e.metaKey) && e.key === 'Enter') {
      e.preventDefault();
      handleSubmit();
    }
  }

  const snapclose = () => {
    setSnap({ snapopen: false });
  }

  const columns = useMemo(
    () => (results.length > 0 ? Object.keys(results[0]) : []),
    [results]
  );

  const filteredResults = useMemo(() => {
    if (!search.trim()) return results;
    const needle = search.trim().toLowerCase();
    return results.filter(row =>
      Object.values(row).some(cell =>
        cell !== null && cell !== undefined && String(cell).toLowerCase().includes(needle)
      )
    );
  }, [results, search]);

  return (
    <>
      <SnapBarError
        message={snap.error}
        snapopen={snap.snapopen}
        snapcolor={snap.snapcolor}
        snapclose={snapclose}
      />

      <div className="windowContent qe-console" data-type={type}>
          <div className="qe-toolbar">
            <div className="qe-field">
              <label htmlFor="qe-type">Query type</label>
              <select
                id="qe-type"
                className="qe-type-select"
                value={type}
                onChange={(e) => setType(e.target.value)}
              >
                <option value="select">Select</option>
                {validUser && <option value="insert">Insert</option>}
                {validUser && <option value="update">Update</option>}
                {validUser && <option value="delete">Delete</option>}
                {validUser && <option value="script">Script (many statements / .sql file)</option>}
              </select>
            </div>

            {type === 'select' ? (
              <div className="qe-field">
                <label htmlFor="qe-limit">Row limit</label>
                <input
                  id="qe-limit"
                  type="number"
                  className="qe-limit-input"
                  min={1}
                  max={5000}
                  value={limit}
                  onChange={(e) => setLimit(e.target.value)}
                />
              </div>
            ) : (
              <span className="qe-risk-badge">
                {isScript ? 'Insert / Update / Delete only' : ['update', 'delete'].includes(type) ? 'WHERE required' : 'Write statement'}
              </span>
            )}

            {isScript && (
              <>
                <input ref={fileRef} type="file" accept=".sql,.txt" className="qe-file-input" onChange={handleFile} />
                <button type="button" className="qe-ghost-btn" onClick={() => fileRef.current && fileRef.current.click()} disabled={loading}>
                  <FontAwesomeIcon icon={faUpload} size="xs" /> Upload .sql file
                </button>
                <label className="qe-check">
                  <input type="checkbox" checked={atomic} onChange={(e) => setAtomic(e.target.checked)} disabled={loading} />
                  All or nothing (roll back everything if one statement fails)
                </label>
              </>
            )}
          </div>

          <div className="qe-editor-shell">
            <div className="qe-editor-bar">
              <div className="qe-dots"><i /><i /><i /></div>
              <span>{isScript && fileName ? fileName : `${TYPE_LABELS[type] || 'SQL'}.sql`}</span>
            </div>
            <textarea
              ref={sqlRef}
              className="qe-sql-textarea"
              rows={isScript ? 14 : 7}
              value={sql}
              onChange={(e) => { setSql(e.target.value); if (isScript) setScriptRun(null); }}
              onKeyDown={handleKeyDown}
              spellCheck={false}
              placeholder={
                isScript
                  ? 'Upload a .sql file, or paste INSERT / UPDATE / DELETE statements separated by ;'
                  : isWrite
                    ? `Write a single ${type.toUpperCase()} statement${['update', 'delete'].includes(type) ? ' (WHERE clause required)' : ''}...`
                    : 'Write a single SELECT statement...'
              }
            ></textarea>
          </div>

          <div className="qe-run-row">
            {isScript && (
              <button type="button" className="qe-ghost-btn" onClick={() => handleScript(false)} disabled={loading}>
                <FontAwesomeIcon icon={faCheck} size="xs" /> Check only
              </button>
            )}
            <button
              type="button"
              className="qe-run-btn"
              onClick={handleSubmit}
              disabled={loading}
            >
              {loading ? <Spinner animation="border" size="sm" /> : <FontAwesomeIcon icon={faPlay} size="xs" />}
              Run {TYPE_LABELS[type] || ''}
            </button>
            <span className="qe-kbd-hint"><kbd>Ctrl</kbd> + <kbd>Enter</kbd> to run</span>
          </div>

          {isScript && processLog.length > 0 && (
            <ol className="qe-process">
              {processLog.map((entry, i) => (
                <li key={i} className={`qe-process-${entry.kind}`}>
                  <time>{entry.time}</time>
                  <span>{entry.text}</span>
                </li>
              ))}
            </ol>
          )}

          {isScript && scriptRun && (
            <div className="qe-table-wrap">
              <table className="qe-table qe-script-table">
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Type</th>
                    <th>Statement</th>
                    <th>Status</th>
                    <th>Rows</th>
                    <th>Time</th>
                    <th>Message</th>
                  </tr>
                </thead>
                <tbody>
                  {scriptRun.statements.map((st) => (
                    <tr key={st.no} data-status={st.status}>
                      <th>{st.no}</th>
                      <td>{st.type}</td>
                      <td title={st.preview}>{st.preview}</td>
                      <td><span className="qe-status">{STATUS_LABELS[st.status] || st.status}</span></td>
                      <td>{st.rows === null || st.rows === undefined ? '' : st.rows}</td>
                      <td>{st.ms === null || st.ms === undefined ? '' : `${st.ms} ms`}</td>
                      <td title={st.error}>{st.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {!isWrite && (
            <div className="qe-results-bar">
              <div className="qe-search-wrap">
                <FontAwesomeIcon icon={faSearch} />
                <input
                  type="text"
                  className="qe-search-input"
                  placeholder="Search results..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  disabled={results.length === 0}
                />
              </div>
              {ranOnce && (
                <div className="qe-row-count">
                  Showing <strong>{filteredResults.length}</strong> of <strong>{results.length}</strong> row{results.length === 1 ? '' : 's'}
                </div>
              )}
              {ranOnce && results.length > 0 && (
                <div className="qe-export">
                  <button type="button" className="qe-ghost-btn" onClick={() => handleExport('csv')} disabled={loading}>
                    <FontAwesomeIcon icon={faDownload} size="xs" /> Export CSV
                  </button>
                  <button type="button" className="qe-ghost-btn" onClick={() => handleExport('sql')} disabled={loading}>
                    <FontAwesomeIcon icon={faDownload} size="xs" /> Export as INSERTs
                  </button>
                </div>
              )}
            </div>
          )}

          {!isWrite && exportNote && <div className="qe-export-note">{exportNote}</div>}

          {!isWrite && ranOnce && results.length === 0 && (
            <div className="qe-empty-state">
              <FontAwesomeIcon icon={faDatabase} />
              No rows returned.
            </div>
          )}

          {!isScript && columns.length > 0 && (
            <div className="qe-table-wrap">
              <table className="qe-table">
                <thead>
                  <tr>
                    <th>#</th>
                    {columns.map(col => <th key={col}>{col}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {filteredResults.map((row, i) => (
                    <tr key={i}>
                      <th>{i + 1}</th>
                      {columns.map(col => <td key={col} title={String(row[col] ?? '')}>{String(row[col] ?? '')}</td>)}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
      </div>
    </>
  );
}

const newTab = (id) => ({ id, type: 'select', loading: false, dirty: false });

const QueryExecutions = () => {
  const nextId = useRef(2);
  const [tabs, setTabs] = useState([newTab(1)]);
  const [activeId, setActiveId] = useState(1);

  const addTab = () => {
    if (tabs.length >= MAX_TABS) return;
    const id = nextId.current++;
    setTabs((list) => [...list, newTab(id)]);
    setActiveId(id);
  };

  const closeTab = (id) => {
    const tab = tabs.find((t) => t.id === id);
    if (tab.dirty && !window.confirm('This tab has a query in it. Close it and lose the query?')) return;
    if (tabs.length === 1) {
      // the last tab is replaced by a fresh one, so the screen is never empty
      const fresh = nextId.current++;
      setTabs([newTab(fresh)]);
      setActiveId(fresh);
      return;
    }
    const index = tabs.findIndex((t) => t.id === id);
    const rest = tabs.filter((t) => t.id !== id);
    setTabs(rest);
    if (id === activeId) setActiveId(rest[Math.min(index, rest.length - 1)].id);
  };

  const updateMeta = (id, meta) => {
    setTabs((list) => {
      const tab = list.find((t) => t.id === id);
      // same state back when nothing changed, so React does not re-render
      if (!tab || (tab.type === meta.type && tab.loading === meta.loading && tab.dirty === meta.dirty)) return list;
      return list.map((t) => (t.id === id ? { ...t, ...meta } : t));
    });
  };

  // numbered among tabs of the same type: Select 1, Select 2, Insert 1 ...
  const labels = {};
  const counts = {};
  tabs.forEach((t) => {
    counts[t.type] = (counts[t.type] || 0) + 1;
    labels[t.id] = `${TYPE_LABELS[t.type] || 'SQL'} ${counts[t.type]}`;
  });

  return (
    <WindowPanel rawHtml={
      <div className="qe-tabs-root">
        <div className="qe-tabbar" role="tablist">
          {tabs.map((t) => (
            <div
              key={t.id}
              role="tab"
              aria-selected={t.id === activeId}
              data-type={t.type}
              className={`qe-tab${t.id === activeId ? ' is-active' : ''}`}
              onClick={() => setActiveId(t.id)}
              onAuxClick={(e) => { if (e.button === 1) { e.preventDefault(); closeTab(t.id); } }}
              title={labels[t.id]}
            >
              {t.loading && <Spinner animation="border" size="sm" />}
              <span className="qe-tab-label">{labels[t.id]}</span>
              <button
                type="button"
                className="qe-tab-close"
                aria-label={`Close ${labels[t.id]}`}
                onClick={(e) => { e.stopPropagation(); closeTab(t.id); }}
              >
                <FontAwesomeIcon icon={faTimes} size="xs" />
              </button>
            </div>
          ))}
          <button
            type="button"
            className="qe-tab-add"
            onClick={addTab}
            disabled={tabs.length >= MAX_TABS}
            title={tabs.length >= MAX_TABS ? `Up to ${MAX_TABS} tabs` : 'New tab'}
          >
            <FontAwesomeIcon icon={faPlus} size="xs" /> New tab
          </button>
        </div>
        {tabs.map((t) => (
          <div key={t.id} hidden={t.id !== activeId}>
            <QueryTab onMeta={(meta) => updateMeta(t.id, meta)} />
          </div>
        ))}
      </div>
    } />
  );
}

export default QueryExecutions;
